import React, { useState } from 'react';
import { firebaseAuth } from '../api';
import firebaseAuthService from '../services/firebaseAuth';

// SVG icons for single-file application
const EyeIcon = ({ size = 24, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const GoogleIcon = ({ size = 20, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AuthForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      if (isRegistering) {
        // Firebase signup
        const result = await firebaseAuthService.signUpWithEmail(email, password);
        if (result && result.user) {
          const idToken = await result.user.getIdToken();
          await firebaseAuth(idToken);
        }
      } else {
        // Firebase login
        const result = await firebaseAuthService.signInWithEmail(email, password);
        if (result && result.user) {
          const idToken = await result.user.getIdToken();
          await firebaseAuth(idToken);
        }
      }
      if (typeof onLogin === 'function') onLogin();
    } catch (e) {
      console.error('Authentication error:', e);
      setError(e.message || "Authentication failed. Please check your email and password.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setError(null);
    setIsLoading(true);
    try {
      console.log('🔐 [AUTH] Starting Google authentication');
      const result = await firebaseAuthService.signInWithGoogle();
      
      if (result && result.user) {
        console.log('✅ [AUTH] Google sign-in successful');
        const idToken = await result.user.getIdToken();
        await firebaseAuth(idToken);
        if (typeof onLogin === 'function') onLogin();
      }
    } catch (e) {
      console.error('❌ [AUTH] Google authentication failed:', e);
      setError(e.message || "Google authentication failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-900 p-4 font-sans">
      <div className="bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md border border-gray-700">
        <div className="flex items-center justify-center mb-6 text-blue-400">
          <EyeIcon size={40} />
          <h2 className="text-3xl font-bold ml-3 text-white">BlackBox</h2>
        </div>
        <h3 className="text-xl font-semibold text-center mb-6 text-gray-200">
          {isRegistering ? 'Register to the System' : 'Log in to the System'}
        </h3>
        <form onSubmit={handleAuth}>
          <div className="mb-4">
            <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              className="bg-gray-700 text-white shadow appearance-none rounded-lg w-full py-3 px-4 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-400 text-sm font-bold mb-2" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="bg-gray-700 text-white shadow appearance-none rounded-lg w-full py-3 px-4 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="text-red-400 text-sm italic mb-4 text-center">{error}</p>}
          <div className="flex flex-col items-center justify-center space-y-4">
            <button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-colors duration-200"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : isRegistering ? 'Register' : 'Log In'}
            </button>
            
            <div className="relative w-full">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-400">Or continue with</span>
              </div>
            </div>
            
            <button
              className="w-full bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-colors duration-200 flex items-center justify-center space-x-2"
              type="button"
              onClick={handleGoogleAuth}
              disabled={isLoading}
            >
              <GoogleIcon size={20} />
              <span>Continue with Google</span>
            </button>
            
            <button
              className="text-sm text-blue-400 hover:text-blue-200 transition-colors duration-200"
              type="button"
              onClick={() => setIsRegistering(!isRegistering)}
            >
              {isRegistering ? 'Already have an account? Log In' : "Don't have an account? Register"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthForm;